import type { TestResult } from './types.js'
import type { WireCaseResult, WireOperationResult } from './wire-protocol.js'

/**
 * The boundary between the wire shapes (`WireCaseResult`, which still carries
 * the closed-legacy snake keys the drivers emit) and the typed results the
 * rest of the app reads (`TestResult`).
 *
 * Every driver emits snake_case `error_type` while the TS types use
 * `errorType`. This is the single normalization point — applied when parsing
 * fresh driver output AND when reading persisted results (historical
 * CodeExecution.testResults rows contain `error_type`). New wire keys are
 * camelCase and need nothing here; see the casing law on `LEGACY_SNAKE_KEYS`.
 */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

export function normalizeTestResults(raw: unknown): TestResult[] {
  if (!Array.isArray(raw)) return []
  return raw.filter(isPlainObject).map((entry) => {
    // Wire shape, plus the camel `errorType` a row that has ALREADY been
    // through here carries: this runs on fresh driver output and on persisted
    // (so possibly already-normalized) rows alike, and must be idempotent.
    const { error_type, operations, ...rest } = entry as Omit<
      WireCaseResult,
      'operations'
    > & {
      errorType?: string
      operations?: unknown
    }
    return {
      ...rest,
      ...((rest.errorType ?? error_type)
        ? { errorType: rest.errorType ?? error_type }
        : {}),
      ...(Array.isArray(operations)
        ? {
            // Per-op legacy error/error_type are no longer part of the typed
            // contract (structured `failure` replaced them). For legacy
            // persisted ops that carry only the strings, synthesize a
            // frameless runtime_error failure here — the single
            // normalization point — so the operations table still renders
            // them; then strip the legacy keys. Everything else on the wire
            // row rides through untouched, so a newly declared camelCase key
            // (rawReturn, LG-669) survives this path at runtime with no edit
            // here — but only the VALUE does: no consumer can read it until
            // `OperationTestResult` declares the field too.
            operations: (operations as WireOperationResult[])
              .filter(isPlainObject)
              .map(({ error: opError, error_type: opSnake, ...op }) => ({
                ...op,
                ...(!op.passed && !op.failure && (opError || opSnake)
                  ? {
                      failure: {
                        kind: 'runtime_error' as const,
                        ...(opSnake ? { errorType: opSnake } : {}),
                        ...(opError ? { message: opError } : {}),
                        userFrames: [],
                      },
                    }
                  : {}),
              })),
          }
        : {}),
    }
  })
}
